import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useClientTable } from "../../../components/table/tableFeatures";
import { css } from "../../../../styled-system/css";
import { Box } from "../../../../styled-system/jsx";
import { Button } from "../../../components/core/basic/Button";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { setCurrentUser } from "../../../redux/reducers/userReducer";
import { setChannel } from "../../../redux/reducers/channel";
import { setMarketPlace } from "../../../redux/reducers/marketPlace";
import { apiClient } from "../../../zodios/client";
import { getOrganizationsOptions } from "../../admin/api/useOrganizations";
import type { Organization } from "../../../zodios/common/orgSchema";

const columnHelper = createColumnBuilder<Organization>();

export function ShopifyStoresTable() {
  const { t } = useTranslation(["onboarding"]);
  const dispatch = useAppDispatch();
  const currentOrgId = useAppSelector((state) => state.user.organization_id);
  const { data: orgs, isLoading } = useQuery(getOrganizationsOptions({ channel: "shopify" }));

  const handleSwitch = useCallback(
    async (orgId: number) => {
      const res = await apiClient.putOrgId({ id: orgId });
      const data = res as { profile_name?: string };
      dispatch(setCurrentUser({ current_profile: data.profile_name }));
      dispatch(setChannel("shopify"));
      localStorage.setItem("channel", "shopify");
      dispatch(setMarketPlace("USA"));
      localStorage.setItem("Marketplace", "USA");
      window.location.reload();
    },
    [dispatch],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor("profile_name", {
        header: t(($) => $.onboarding.shopifyStores.Store),
        enableResizing: false,
      }),
      columnHelper.accessor("name", {
        header: t(($) => $.onboarding.shopifyStores["Org Name"]),
        enableResizing: false,
      }),
      columnHelper.accessor("status", {
        header: t(($) => $.onboarding.shopifyStores.Status),
        size: 100,
        enableResizing: false,
      }),
      columnHelper.display({
        id: "actions",
        header: "",
        size: 100,
        enableResizing: false,
        enableSorting: false,
        meta: { justify: "end" },
        cell: ({ row }) => {
          const isCurrentOrg = String(row.original.id) === currentOrgId;
          if (isCurrentOrg) {
            return (
              <span className={css({ fontSize: "small", color: "blue.600", fontWeight: 500 })}>
                {t(($) => $.onboarding.shopifyStores.Current)}
              </span>
            );
          }
          return (
            <Button variant="secondary" size="small" onPress={() => void handleSwitch(row.original.id)}>
              {t(($) => $.onboarding.shopifyStores.Switch)}
            </Button>
          );
        },
      }),
    ],
    [currentOrgId, handleSwitch, t],
  );

  const table = useClientTable({
    data: orgs,
    columns,
    getRowId: (row) => String(row.id),
    enableColumnResizing: false,
    meta: { showSkeleton: isLoading },
  });

  if (!isLoading && (!orgs || orgs.length === 0)) {
    return <Box padding="medium">{t(($) => $.onboarding.shopifyStores["No Shopify stores found."])}</Box>;
  }

  return <Table table={table} />;
}
